/* eslint-disable no-unused-expressions,no-unused-vars */
/* global PLATFORM, G */
import routeComposition from 'trait/composition/route'
import redirectSequence from 'lib/sequence/module/adapter/router/redirect'
import { asyncPipeSpread, asyncPipeSpreadIf, getFirstItem } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import search from 'app/_shared/events/search'
import { setData } from 'lib/sequence/model/api/set'
import setCreatedFilter from 'app/_shared/action/partial/filter/setDateCreated'
import apply from 'app/_shared/action/partial/filter/apply'
import { checked } from 'lib/sequence/component/state/checked'
import { get } from 'lib/sequence/component/state/value'
import sequenceComponentState from 'lib/sequence/component/state'
import { hide } from 'lib/sequence/component/state/hidden'

const {
  get: getActiveTab,
} = sequenceComponentState('activeTab')

/**
 * Sets incoming item and installedAt models' data to the current measurement model.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(...[*]): Promise<*>}
 */
const presetItem = module => async (components, ...args) => {
  const model = module[G.MODEL]

  const [itemData] = args || []
  const { item, installedAt } = itemData || {}

  if (item && installedAt) {
    const itemKey = item[G.STATE][G.REF]
    const itemCache = item[G.CACHE]?.value || {}
    const orgKey = installedAt[G.STATE][G.REF]
    const orgCache = installedAt[G.CACHE]?.[0]?.value || {}

    setData(model, {
      measurement: {
        project: itemCache.serial,
        customer: orgCache.name,
        item: [itemKey],
        organisation: [orgKey],
      }
    }, { updateCache: true })
  }

  return [components, ...args]
}

/**
 * Determines whether to show the teaser page or content.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayTeaser = module => async (components, ...args) => {
  const { data = false } = module[G.STATE]
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]

  const hideTeaserPage = data || getFirstItem(
    await search(null, module, actionComponent, { detail: {
      query: 'metatype:x_eco_apmsensor OR metatype:x_eco_apmpairing OR metatype:x_eco_apmmeasurement',
    } }),
  )

  if (!hideTeaserPage) {
    module[G.STATE][G.ROUTE] = routeComposition(null, 'teaser')
    await redirectSequence(module)({})
  }

  return [components, { displayTeaser: !hideTeaserPage }, ...args]
}

/**
 * Sets the `project` filter according to the current UI state.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @return {function(*, {filter?: {}}=, ...[*]): [undefined,{filter: {}},...*[]]}
 */
export const setProjectFilter = (module) => (components, { filter = {} } = {}, ...args) => {
  const { project } = components

  const accumulatedFilter = filter
  const projectValue = get(project)

  projectValue && (accumulatedFilter.project = projectValue)

  return [components, { filter: accumulatedFilter }, ...args]
}

/**
 * Sets the `reference` filter according to the current UI state.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @return {function(*, {filter?: {}}=, ...[*]): [undefined,{filter: {}},...*[]]}
 */
export const setReferenceFilter = (module) => (components, { filter = {} } = {}, ...args) => {
  const { reference } = components

  const accumulatedFilter = filter
  const isReference = checked(reference)

  isReference && (accumulatedFilter.reference = true)

  return [components, { filter: accumulatedFilter }, ...args]
}

/**
 * Shows the filter quick action if the current `activeTab` of the `tabs` element is `measurements`.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @return {function(*, ...[*]): Promise<[*,...{filter?: {}}[]]>}
 */
const displayFilterQuickAction = (module) => async (components, { filter = {} } = {}, ...args) => {
  const { quickActions = [] } = module[G.STATE][G.ACTION][G.UI]
  const filterAction = quickActions.find((quickAction) => quickAction.key === 'filter')

  const { tabs } = components
  const { activeTab } = tabs[G.STATE]

  filterAction.hidden = activeTab !== 1
  filterAction.count = Object.keys(filter).length

  return [components, { filter }, ...args]
}

/**
 * Shows the "Pair device" action button if the current tab is `0`, otherwise, it hides it.
 *
 * @param module  current module
 * @return {function(*, ...[*]): *[]}
 */
const displayConnectButton = (module) => (components, ...args) => {
  const { component, tabs } = components
  const [btnPair] = component[G.ACTIONS]
  const activeTab = getActiveTab(tabs)

  hide(btnPair, activeTab !== 0)

  return [components, ...args]
}

/**
 * xEcoApm Index Action
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  displayTeaser(module),
  asyncPipeSpreadIf((components, { displayTeaser }) => !displayTeaser)(
    presetItem(module),
    setProjectFilter(module),
    setCreatedFilter(module),
    setReferenceFilter(module),
    asyncPipeSpreadIf(() => PLATFORM === 'mobile')(
      displayFilterQuickAction(module),
      displayConnectButton(module),
    ),
    apply(module),
  )
)(find(component), ...args)
